import { Job } from '@/types/job';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Calendar, Building, Clock, Euro, Timer, Calendar as CalendarIcon } from 'lucide-react';
import { extractJobInfo } from '@/utils/jobInfoExtractor';
import { Link } from 'react-router-dom';
interface JobCardProps {
  job: Job;
  onClick?: () => void;
}
export const JobCard = ({
  job,
  onClick
}: JobCardProps) => {
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('de-DE');
    } catch {
      return dateString;
    }
  };

  // Extract first image from description if available
  const extractImageFromDescription = (description: string) => {
    const imgMatch = description.match(/background-image:\s*url\("([^"]+)"\)|<img[^>]+src="([^"]+)"/i);
    return imgMatch ? imgMatch[1] || imgMatch[2] : null;
  };
  const imageUrl = job.imageUrl || job.companyLogo || extractImageFromDescription(job.description) || 'https://k61286.coveto.de/public_data/jobs/fotos/Restaurantleitung-hippes-Konzept.png';
  const jobInfo = extractJobInfo(job.description);
  return <Link to={`/stellenangebote/${job.id}`} className="block">
      <Card className="group cursor-pointer overflow-hidden bg-card/50 backdrop-blur-sm border border-border/20 hover:border-primary/40 transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 animate-fade-in h-full">
        {/* Header Image Section */}
        <div className="relative h-48 sm:h-56 md:h-64 lg:h-72 overflow-hidden">
          <img 
            src={imageUrl} 
            alt={`Stellenangebot: ${job.title}`}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10 group-hover:from-black/70"></div>
          
          {/* Company Badge */}
          <div className="absolute top-3 left-3 sm:top-4 sm:left-4 transform transition-all duration-300 group-hover:scale-105">
            
          </div>
          
          {/* Job Type Badge */}
          {job.employmentType && <div className="absolute top-3 right-3 sm:top-4 sm:right-4 transform transition-all duration-300 group-hover:scale-105">
              <Badge className="bg-primary/95 backdrop-blur-sm text-white border-0 font-medium shadow-lg px-2 py-1 sm:px-3 sm:py-1.5 text-xs sm:text-sm">
                {job.employmentType}
              </Badge>
            </div>}
          
          {/* Bottom content overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 lg:p-6 text-white">
            <h3 className="font-display font-bold text-lg sm:text-xl lg:text-2xl leading-tight mb-2 sm:mb-3 group-hover:text-primary-glow transition-all duration-300 drop-shadow-lg line-clamp-2">
              {job.title}
            </h3>
            
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 lg:gap-4 text-xs sm:text-sm opacity-95">
              {job.location && <div className="flex items-center gap-1.5 sm:gap-2 bg-black/20 backdrop-blur-sm rounded-full px-2 py-1 sm:px-3 sm:py-1.5">
                  <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="font-medium truncate max-w-[100px] sm:max-w-none">{job.location}</span>
                </div>}
              {job.publishDate && <div className="flex items-center gap-1.5 sm:gap-2 bg-black/20 backdrop-blur-sm rounded-full px-2 py-1 sm:px-3 sm:py-1.5">
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="font-medium">{formatDate(job.publishDate)}</span>
                </div>}
            </div>
          </div>
        </div>
        
        {/* Content Section */}
        <CardContent className="p-3 sm:p-4 lg:p-6 bg-gradient-to-b from-card/80 to-card flex-1">
          <div className="space-y-3 sm:space-y-4 h-full flex flex-col">
            {/* Additional Details */}
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {job.department && <Badge variant="outline" className="text-xs font-medium bg-muted/50 border-border hover:bg-muted transition-colors">
                  <Building className="w-3 h-3 mr-1 sm:mr-1.5" />
                  <span className="truncate max-w-[80px] sm:max-w-none">{job.department}</span>
                </Badge>}
              
              {job.salary && <Badge variant="outline" className="text-xs font-medium bg-muted/50 border-border hover:bg-muted transition-colors">
                  <Euro className="w-3 h-3 mr-1 sm:mr-1.5" />
                  <span className="truncate max-w-[60px] sm:max-w-none">{job.salary}</span>
                </Badge>}
              
              {(jobInfo.workingTime || job.employmentType) && <Badge variant="outline" className="text-xs font-medium bg-muted/50 border-border hover:bg-muted transition-colors">
                  <Clock className="w-3 h-3 mr-1 sm:mr-1.5" />
                  <span className="truncate max-w-[80px] sm:max-w-none">{jobInfo.workingTime || job.employmentType}</span>
                </Badge>}
            </div>
            
            {/* Application Deadline */}
            {job.applicationDeadline && <div className="pt-2 sm:pt-3 border-t border-border/40 mt-auto">
                <p className="text-xs text-muted-foreground font-medium flex items-center gap-1.5 sm:gap-2">
                  <CalendarIcon className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">Bewerbungsschluss: {formatDate(job.applicationDeadline)}</span>
                </p>
              </div>}
          </div>
        </CardContent>
      </Card>
    </Link>;
};